import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { ArrowUp, Send, ArrowLeft, Trash2 } from "lucide-react";
import { TAG_COLORS, timeAgo } from "../components/ForumPostCard";
import ShareMenu from "../components/ShareMenu";
import { avatarUrl } from "../lib/avatar";

function renderBody(text) {
  // Simple markdown: **bold**, newlines → paragraphs, --- → divider
  const lines = text.split("\n");
  return lines.map((line, i) => {
    if (line.trim() === "---") return <hr key={i} className="border-ink/20 my-3" />;
    if (!line.trim()) return <br key={i} />;
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <span key={i}>
        {parts.map((part, j) =>
          part.startsWith("**") && part.endsWith("**")
            ? <strong key={j} className="font-bold">{part.slice(2, -2)}</strong>
            : part
        )}
        {"\n"}
      </span>
    );
  });
}

export default function ForumPostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post,    setPost]    = useState(null);
  const [replies, setReplies] = useState([]);
  const [body,    setBody]    = useState("");
  const [voted,   setVoted]   = useState(false);

  useEffect(() => {
    (async () => {
      const [postRes, repliesRes] = await Promise.all([
        supabase.from("forum_posts")
          .select("*, author:profiles!user_id(id,name,avatar_url,participant_id), forum_post_votes(user_id)")
          .eq("id", id).single(),
        supabase.from("forum_replies")
          .select("*, author:profiles!user_id(id,name,avatar_url,participant_id)")
          .eq("post_id", id).order("created_at", { ascending: true }),
      ]);
      if (postRes.data) {
        const p = postRes.data;
        setPost(p);
        setVoted((p.forum_post_votes || []).some((v) => v.user_id === user?.id));
      }
      setReplies(repliesRes.data || []);
    })();
  }, [id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onVote = async () => {
    if (!user || !post) return;
    const wasVoted = voted;
    setVoted(!wasVoted);
    setPost((p) => ({ ...p, upvotes: wasVoted ? p.upvotes - 1 : p.upvotes + 1 }));
    if (wasVoted) {
      await supabase.from("forum_post_votes").delete().eq("post_id", post.id).eq("user_id", user.id);
    } else {
      await supabase.from("forum_post_votes").insert({ post_id: post.id, user_id: user.id });
    }
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!body.trim() || !user) return;
    const { data } = await supabase.from("forum_replies")
      .insert({ post_id: post.id, user_id: user.id, body: body.trim() })
      .select("*, author:profiles!user_id(id,name,avatar_url,participant_id)")
      .single();
    if (data) {
      setReplies((r) => [...r, data]);
      setPost((p) => ({ ...p, reply_count: (p.reply_count || 0) + 1 }));
      setBody("");
    }
  };

  const deleteReply = async (replyId) => {
    await supabase.from("forum_replies").delete().eq("id", replyId);
    setReplies((r) => r.filter((x) => x.id !== replyId));
    setPost((p) => ({ ...p, reply_count: Math.max(0, (p.reply_count || 1) - 1) }));
  };

  if (!post) return <div className="p-10 font-hand text-3xl">loading discussion…</div>;

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <Link to="/forum" className="brutal-btn text-sm flex items-center gap-2 w-fit" data-testid="back-to-forum">
        <ArrowLeft size={14} /> Back to Forum
      </Link>

      {/* POST CARD */}
      <article className="brutal-card p-6 sm:p-8">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {post.is_pinned && (
            <span className="font-mono text-[10px] uppercase tracking-widest bg-ink text-cream px-2 py-0.5">📌 PINNED</span>
          )}
          {(post.tags || []).map((t) => {
            const style = TAG_COLORS[t] || { bg: "#FFD600", color: "#121212" };
            return <span key={t} className="tag-pill" style={{ background: style.bg, color: style.color }}>#{t}</span>;
          })}
        </div>

        <h1 className="font-display font-extrabold text-3xl sm:text-4xl uppercase tracking-tight leading-tight mb-4">
          {post.title}
        </h1>

        {/* Author */}
        <div className="flex items-center gap-3 mb-6">
          <Link to={`/u/${post.author?.id}`}>
            <img src={avatarUrl(post.author?.avatar_url, post.author?.id)} alt={post.author?.name} className="w-10 h-10 border-2 border-ink blob bg-cream" />
          </Link>
          <div>
            <Link to={`/u/${post.author?.id}`} className="font-display font-extrabold uppercase tracking-tight hover:underline">
              {post.author?.name}
            </Link>
            <div className="font-mono text-xs text-ink/50">{post.author?.participant_id} · {timeAgo(post.created_at)}</div>
          </div>
        </div>

        {/* Body */}
        <div className="prose-like leading-relaxed whitespace-pre-line text-ink/90 text-base mb-6">
          {renderBody(post.body)}
        </div>

        {/* Vote button */}
        <div className="flex items-center gap-4 border-t-2 border-ink pt-4">
          <button
            onClick={onVote}
            disabled={!user}
            className={`brutal-btn flex items-center gap-2 ${voted ? "brutal-btn-yellow" : ""}`}
            data-testid="post-vote-btn"
          >
            <ArrowUp size={16} strokeWidth={voted ? 3 : 2} />
            <span className="font-display font-extrabold text-lg">{post.upvotes}</span>
            <span className="text-xs">upvotes</span>
          </button>
          <span className="font-mono text-sm text-ink/50">
            {post.reply_count} {post.reply_count === 1 ? "reply" : "replies"}
          </span>
          <ShareMenu
            url={`/forum/${post.id}`}
            title={post.title}
          />
          {!user && (
            <Link to="/login" className="ml-auto text-sm font-mono underline underline-offset-2">Log in to vote</Link>
          )}
        </div>
      </article>

      {/* REPLIES */}
      <section className="brutal-card p-5 sm:p-6">
        <h2 className="font-display font-extrabold text-2xl uppercase tracking-tight mb-5">
          {replies.length} {replies.length === 1 ? "Reply" : "Replies"}
        </h2>

        {user ? (
          <form onSubmit={submitReply} className="flex gap-2 mb-6" data-testid="reply-form">
            <textarea
              className="brutal-input flex-1 min-h-[80px] text-sm resize-none"
              placeholder="Share your take…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="reply-input"
            />
            <button type="submit" className="brutal-btn brutal-btn-yellow h-fit self-end" data-testid="reply-submit">
              <Send size={14} /> Post
            </button>
          </form>
        ) : (
          <Link to="/login" className="brutal-btn mb-6 inline-flex">Log in to reply</Link>
        )}

        <div className="space-y-4">
          {replies.map((r) => (
            <div key={r.id} className="flex gap-3" data-testid={`reply-${r.id}`}>
              <Link to={`/u/${r.author?.id}`} className="shrink-0">
                <img src={avatarUrl(r.author?.avatar_url, r.author?.id)} alt={r.author?.name} className="w-9 h-9 border-2 border-ink blob bg-cream" />
              </Link>
              <div className="brutal-card-sm p-3 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap mb-1">
                  <Link to={`/u/${r.author?.id}`} className="font-display font-extrabold uppercase tracking-tight hover:underline">
                    {r.author?.name}
                  </Link>
                  <span className="font-mono text-[11px] text-ink/50">{r.author?.participant_id}</span>
                  <span className="font-mono text-[11px] text-ink/50">·</span>
                  <span className="font-mono text-[11px] text-ink/50">{timeAgo(r.created_at)}</span>
                  {user?.id === r.user_id && (
                    <button onClick={() => deleteReply(r.id)} className="ml-auto brutal-btn text-[10px] px-1.5 py-0.5">
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
                <div className="leading-relaxed text-sm whitespace-pre-line">{r.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
